/**
 * Pagarle al técnico tercerizado.
 *
 * Es el único momento en que el costo del técnico deja de ser un número para
 * cotizar y se convierte en plata que salió. Sin este paso la ganancia del
 * taller es una cuenta que nadie asienta: el costo estaba cargado, pero el
 * egreso no figuraba en ningún lado y la caja mostraba de más.
 *
 * El importe arranca en lo que dice el presupuesto, convertido a la moneda de
 * la caja de donde sale. Se puede pisar: lo que vale es lo que se pagó de
 * verdad, no lo que se había estimado.
 */
import { useEffect, useState } from "react";
import { convertir } from "@tora/shared";

import { EditorPagos, nuevoPago } from "@/components/cobro/EditorPagos";
import type { CuentaFinanciera, PagoCobro } from "@/components/cobro/logica";
import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { MedioPago, PagoTecnicoOt } from "@/lib/tipos";
import { aNumero } from "@/lib/tipos";

import { leerPlata, nombreOrden, type OrdenTaller } from "./logica";

export interface DialogPagarTecnicoProps {
  orden: OrdenTaller | null;
  cuentas: CuentaFinanciera[];
  /**
   * El dólar del TÉCNICO: el de referencia sin el ajuste fijo del mostrador,
   * el mismo que usa `pagar_tecnico_ot()` por defecto (migración 0054).
   */
  cotizacion: number;
  guardando?: boolean;
  onCerrar: () => void;
  onConfirmar: (pago: PagoTecnicoOt) => void;
}

export function DialogPagarTecnico({
  orden,
  cuentas,
  cotizacion,
  guardando = false,
  onCerrar,
  onConfirmar,
}: DialogPagarTecnicoProps): JSX.Element {
  const [pagos, setPagos] = useState<PagoCobro[]>([]);

  const costo = orden ? aNumero(orden.costo_tecnico_importe) : 0;
  const monedaCosto = orden?.costos_moneda ?? "USD";

  useEffect(() => {
    if (!orden) {
      setPagos([]);
      return;
    }
    const p = nuevoPago(cuentas, [], costo, monedaCosto, cotizacion);
    setPagos(p ? [p] : []);
  }, [orden, cuentas, cotizacion, costo, monedaCosto]);

  const pago = pagos[0];
  const importePago = pago?.importe ?? null;
  const puede = importePago !== null && importePago > 0;
  const plata = orden ? leerPlata(orden) : null;

  // Lo que queda para el local despues de pagarle al tecnico, con el pago que
  // se esta por hacer y no con el estimado.
  const cuenta = pago ? cuentas.find((c) => c.id === pago.cuenta_id) : undefined;
  const pagoEnMonedaOrden =
    importePago !== null && cuenta
      ? convertir(importePago, cuenta.moneda, monedaCosto, cotizacion)
      : 0;
  const quedaParaElLocal = plata?.precio ? plata.precio - pagoEnMonedaOrden : null;

  return (
    <Dialog open={orden !== null} onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent tamano="md">
        {orden ? (
          <>
            <DialogHeader>
              <DialogTitle>Pagarle al técnico</DialogTitle>
              <DialogDescription>
                {nombreOrden(orden)} · {orden.equipo}
                {orden.tercerizado ? ` · ${orden.tercerizado}` : ""}. Sale de la caja ahora.
              </DialogDescription>
            </DialogHeader>

            <DialogBody>
              <Card variant="muted" padding="compacto">
                <dl className="space-y-1 text-sm">
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-muted-foreground">Lo que cotizó el técnico</dt>
                    <dd>
                      <Importe monto={costo} moneda={monedaCosto} className="font-semibold" />
                    </dd>
                  </div>
                  {plata?.precio ? (
                    <div className="flex items-baseline justify-between gap-4">
                      <dt className="text-muted-foreground">Precio al cliente</dt>
                      <dd>
                        <Importe monto={plata.precio} moneda={plata.moneda} />
                      </dd>
                    </div>
                  ) : null}
                </dl>
              </Card>

              <EditorPagos
                pagos={pagos}
                onChange={setPagos}
                cuentas={cuentas}
                objetivo={costo}
                monedaObjetivo={monedaCosto}
                cotizacion={cotizacion}
                unoSolo
              />

              <p className="text-xs text-muted-foreground">
                Al técnico se le paga en pesos, al dólar de referencia sin el ajuste fijo del
                mostrador. Se registra una sola vez: si pagaste distinto de lo cotizado, poné lo que
                pagaste de verdad, que es lo que va a decir la caja.
              </p>

              {/* Lo que queda para el local. Que pierda lo dice el tono del
                  aviso; el numero lleva el color de su moneda. */}
              {quedaParaElLocal !== null ? (
                <Aviso tono={quedaParaElLocal < 0 ? "warning" : "neutral"} compacto>
                  {quedaParaElLocal < 0 ? (
                    <>
                      Con este pago la reparación pierde{" "}
                      <Importe monto={-quedaParaElLocal} moneda={monedaCosto} tamano="sm" />.
                    </>
                  ) : (
                    <>
                      Para el local quedan{" "}
                      <Importe monto={quedaParaElLocal} moneda={monedaCosto} tamano="sm" />.
                    </>
                  )}
                </Aviso>
              ) : null}
            </DialogBody>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onCerrar}>
                Cancelar
              </Button>
              <Button
                type="button"
                cargando={guardando}
                disabled={!puede}
                onClick={() => {
                  if (!pago || pago.importe === null) return;
                  onConfirmar({
                    cuenta_id: pago.cuenta_id,
                    medio: pago.medio as MedioPago,
                    importe: pago.importe,
                  });
                }}
              >
                Registrar el pago
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
